import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, FileText, Trash2, TrendingUp, Filter, User, Calendar, Users, Download, FileDown, MessageSquare, CheckCircle } from 'lucide-react';
import AttendanceGridReport from '@/components/AttendanceGridReport';
import ChildAvatar from '@/components/ChildAvatar';
import { matchesSearch } from '@/utils/searchUtils';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '@/contexts/AuthContext';
import { addPdfHeader, addPdfHeaderAllPages, getPdfHeaderHeight } from '@/utils/pdfHelper';

const Reports = () => {
  const { hasPermission, isAdmin } = useAuth();
  const canExportReports = hasPermission('reports_export') || isAdmin();
  
  const [children, setChildren] = useState([]);
  const [activities, setCourses] = useState([]);
  const [childReports, setChildReports] = useState([]);
  const [activityReports, setCourseReports] = useState([]);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [individualReport, setIndividualReport] = useState(null);
  const [gridReport, setGridReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activityDialogOpen, setCourseDialogOpen] = useState(false);
  const [reportMonth, setReportMonth] = useState(new Date().toISOString().slice(0, 7));
  const [reportType, setReportType] = useState('group'); // 'group' or 'individual'
  const [selectedChild, setSelectedChild] = useState('');
  const [childSearchTerm, setChildSearchTerm] = useState(''); // Novo estado para filtro de busca
  
  // Estado para o relatório de cadastros ativos
  const [activeChildrenReport, setActiveChildrenReport] = useState(null);
  const [activeReportStartDate, setActiveReportStartDate] = useState('');
  const [activeReportEndDate, setActiveReportEndDate] = useState('');
  const [activeReportLoading, setActiveReportLoading] = useState(false);
  const [activeReportOrigins, setActiveReportOrigins] = useState([]); // Filtro de origens do relatório de ativos
  
  // Estado para o relatório de aniversariantes
  const [birthdayReport, setBirthdayReport] = useState(null);
  const [birthdayStartDate, setBirthdayStartDate] = useState('');
  const [birthdayEndDate, setBirthdayEndDate] = useState('');
  const [birthdayLoading, setBirthdayLoading] = useState(false);
  const [birthdayOrigins, setBirthdayOrigins] = useState(['livre_demanda', 'gov_celso_ramos', 'biguacu']);
  const [birthdayShifts, setBirthdayShifts] = useState(['Matutino', 'Vespertino']);
  const [birthdayDayPatterns, setBirthdayDayPatterns] = useState(['seg_sex', 'ter_qui', 'qua_sex']);
  
  // Estado para o relatório de lista de crianças customizado
  const [childListReport, setChildListReport] = useState(null);
  const [childListLoading, setChildListLoading] = useState(false);
  const [childListColumns, setChildListColumns] = useState(['enrollment_number', 'name', 'shift', 'days_of_week', 'age']);
  const [childListOrigins, setChildListOrigins] = useState(['livre_demanda', 'gov_celso_ramos', 'biguacu']);
  const [childListShifts, setChildListShifts] = useState(['Matutino', 'Vespertino']);
  const [childListDays, setChildListDays] = useState(['ter_qui', 'qua_sex', 'seg_sex']);
  const [childListGenders, setChildListGenders] = useState(['MASCULINO', 'FEMININO']);
  const [childListAges, setChildListAges] = useState([]);
  const [childListStatus, setChildListStatus] = useState('active'); // 'active', 'inactive', 'both'
  const [childListSortBy, setChildListSortBy] = useState('name');
  const [childListSortOrder, setChildListSortOrder] = useState('asc');
  
  // Filtros (mesmos da página de Chamada)
  const [selectedOrigins, setSelectedOrigins] = useState(['livre_demanda', 'gov_celso_ramos', 'biguacu']);
  const [selectedShifts, setSelectedShifts] = useState(['Matutino', 'Vespertino']);
  const [selectedDays, setSelectedDays] = useState(['ter_qui', 'qua_sex', 'seg_sex']);
  const [selectedAges, setSelectedAges] = useState(['6-8', '9-11', '12-15']);
  
  // Filtros por atividade/turma
  const [allActivities, setAllActivities] = useState([]);
  const [selectedActivityId, setSelectedActivityId] = useState('');
  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState([]);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);
  
  // Intervalo de datas
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Ordenação
  const [sortBy, setSortBy] = useState('name'); // 'name', 'age', 'attendance_rate'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'
  
  const origins = [
    { value: 'livre_demanda', label: 'Livre Demanda' },
    { value: 'gov_celso_ramos', label: 'Governador Celso Ramos' },
    { value: 'biguacu', label: 'Biguaçu' }
  ];

  const shifts = [
    { value: 'Matutino', label: 'Manhã' },
    { value: 'Vespertino', label: 'Tarde' }
  ];

  const dayPatterns = [
    { value: 'ter_qui', label: 'Ter e Qui' },
    { value: 'qua_sex', label: 'Qua e Sex' },
    { value: 'seg_sex', label: 'Seg a Sex' }
  ];

  // Opções de colunas para o relatório de lista de crianças
  const childListColumnOptions = [
    { value: 'enrollment_number', label: 'Matrícula' },
    { value: 'name', label: 'Aluno' },
    { value: 'shift', label: 'Turno' },
    { value: 'days_of_week', label: 'Dias' },
    { value: 'gender', label: 'Gênero' },
    { value: 'birth_date', label: 'Data de Nascimento' },
    { value: 'age', label: 'Idade' },
    { value: 'guardian_phone', label: 'Telefone' },
    { value: 'guardian_name', label: 'Responsável' },
    { value: 'guardian_relationship', label: 'Parentesco' },
    { value: 'enrollment_origin', label: 'Origem' },
    { value: 'neighborhood', label: 'Bairro' }
  ];

  // Opções de ordenação para o relatório de lista
  const childListSortOptions = [
    { value: 'name', label: 'Nome (Alfabética)' },
    { value: 'age', label: 'Idade' },
    { value: 'enrollment_number', label: 'Matrícula' },
    { value: 'shift', label: 'Turno' },
    { value: 'days_of_week', label: 'Dias' },
    { value: 'gender', label: 'Gênero' },
    { value: 'enrollment_origin', label: 'Origem' }
  ];

  const weekDays = [
    { value: 'segunda', label: 'Segunda' },
    { value: 'terca', label: 'Terça' },
    { value: 'quarta', label: 'Quarta' },
    { value: 'quinta', label: 'Quinta' },
    { value: 'sexta', label: 'Sexta' }
  ];

  const ageRanges = [
    { value: '6-8', label: '6 a 8 anos' },
    { value: '9-11', label: '9 a 11 anos' },
    { value: '12-15', label: '12 a 15 anos' }
  ];
  
  const [childReportForm, setChildReportForm] = useState({
    child_id: '',
    report_type: 'comportamental',
    title: '',
    description: '',
    severity: 'baixa',
    date: new Date().toISOString().split('T')[0]
  });

  const [activityReportForm, setCourseReportForm] = useState({
    activity_id: '',
    title: '',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Limpar relatórios ao mudar tipo
    setGridReport(null);
    setMonthlyReport(null);
    setIndividualReport(null);
  }, [reportType]);

  // Função de ordenação
  const sortReportData = (reportData) => {
    if (!reportData || !reportData.report) return reportData;
    
    const sortedReport = [...reportData.report].sort((a, b) => {
      let comparison = 0;
      
      if (sortBy === 'name') {
        comparison = (a.child_name || '').localeCompare(b.child_name || '');
      } else if (sortBy === 'age') {
        comparison = (a.age || 0) - (b.age || 0);
      } else if (sortBy === 'attendance_rate') {
        comparison = (a.attendance_rate || 0) - (b.attendance_rate || 0);
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return {
      ...reportData,
      report: sortedReport
    };
  };

  // Computar o relatório ordenado sem causar re-renderizações
  const sortedGridReport = gridReport ? sortReportData(gridReport) : null;

  const fetchData = async () => {
    try {
      const [childrenRes, activitiesRes, childReportsRes, activityReportsRes] = await Promise.all([
        axios.get(`${API}/children`),
        axios.get(`${API}/activities`),
        axios.get(`${API}/reports`),
        axios.get(`${API}/course-reports`)
      ]);
      setChildren(childrenRes.data);
      setCourses(activitiesRes.data);
      setAllActivities(activitiesRes.data);
      setChildReports(childReportsRes.data);
      setCourseReports(activityReportsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleChildReportSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/reports`, childReportForm);
      toast.success('Relatório criado com sucesso!');
      setDialogOpen(false);
      resetChildReportForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar relatório');
    }
  };

  const handleCourseReportSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/course-reports`, activityReportForm);
      toast.success('Relatório do atividade criado com sucesso!');
      setCourseDialogOpen(false);
      resetCourseReportForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar relatório');
    }
  };

  const handleDeleteChildReport = async (id) => {
    if (window.confirm('Deseja realmente excluir este relatório?')) {
      try {
        await axios.delete(`${API}/reports/${id}`);
        toast.success('Relatório removido!');
        fetchData();
      } catch (error) {
        toast.error('Erro ao remover relatório');
      }
    }
  };

  const handleDeleteCourseReport = async (id) => {
    if (window.confirm('Deseja realmente excluir este relatório?')) {
      try {
        await axios.delete(`${API}/course-reports/${id}`);
        toast.success('Relatório removido!');
        fetchData();
      } catch (error) {
        toast.error('Erro ao remover relatório');
      }
    }
  };

  const generateMonthlyReport = async () => {
    try {
      const params = {};
      
      if (startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      } else {
        params.month = reportMonth;
      }
      
      // Filtros básicos
      params.origins = selectedOrigins.join(',');
      params.shifts = selectedShifts.join(',');
      params.days = selectedDays.join(',');
      params.ages = selectedAges.join(',');
      
      // Filtros por atividade
      if (selectedActivityId) params.activity_id = selectedActivityId;
      if (selectedDaysOfWeek.length > 0) params.day_of_week = selectedDaysOfWeek.join(',');
      if (selectedTimeSlots.length > 0) params.time_slot = selectedTimeSlots.join(',');
      
      const response = await axios.get(`${API}/attendance/report/grid`, { params });
      setGridReport(response.data);
      setMonthlyReport(null);
      setIndividualReport(null);
      toast.success('Relatório gerado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar relatório mensal');
    }
  };

  const generateIndividualReport = async () => {
    if (!selectedChild) {
      toast.error('Selecione uma criança');
      return;
    }
    
    try {
      const params = { child_id: selectedChild };
      
      if (startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      } else {
        params.month = reportMonth;
      }
      
      // Usar o endpoint de grade para manter o mesmo formato
      const response = await axios.get(`${API}/attendance/report/grid`, { 
        params: {
          ...params,
          origins: 'livre_demanda,gov_celso_ramos,biguacu',
          shifts: 'Matutino,Vespertino',
          days: 'ter_qui,qua_sex,seg_sex',
          ages: '6-8,9-11,12-15'
        }
      });
      
      // Filtrar apenas a criança selecionada
      const childReport = response.data.report.find(r => r.child_id === selectedChild);
      
      if (childReport) {
        setGridReport({
          ...response.data,
          report: [childReport],
          total_children: 1
        });
      }
      
      setMonthlyReport(null);
      setIndividualReport(null);
      toast.success('Relatório individual gerado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar relatório individual');
    }
  };

  const toggleOriginFilter = (origin) => {
    setSelectedOrigins(prev => 
      prev.includes(origin)
        ? prev.filter(o => o !== origin)
        : [...prev, origin]
    );
  };

  const selectAllOrigins = () => {
    setSelectedOrigins(['livre_demanda', 'gov_celso_ramos', 'biguacu']);
  };

  const toggleShiftFilter = (shift) => {
    setSelectedShifts(prev => 
      prev.includes(shift)
        ? prev.filter(s => s !== shift)
        : [...prev, shift]
    );
  };

  const selectAllShifts = () => {
    setSelectedShifts(['Matutino', 'Vespertino']);
  };

  const toggleDaysFilter = (days) => {
    setSelectedDays(prev => 
      prev.includes(days)
        ? prev.filter(d => d !== days)
        : [...prev, days]
    );
  };

  const selectAllDays = () => {
    setSelectedDays(['ter_qui', 'qua_sex', 'seg_sex']);
  };

  const toggleAgeFilter = (age) => {
    setSelectedAges(prev => 
      prev.includes(age)
        ? prev.filter(a => a !== age)
        : [...prev, age]
    );
  };

  const selectAllAges = () => {
    setSelectedAges(['6-8', '9-11', '12-15']);
  };

  const resetChildReportForm = () => {
    setChildReportForm({
      child_id: '',
      report_type: 'comportamental',
      title: '',
      description: '',
      severity: 'baixa',
      date: new Date().toISOString().split('T')[0]
    });
  };

  const resetCourseReportForm = () => {
    setCourseReportForm({
      activity_id: '',
      title: '',
      description: '',
      date: new Date().toISOString().split('T')[0]
    });
  };

  const getChildName = (childId) => {
    const child = children.find(c => c.id === childId);
    return child ? child.name : 'N/A';
  };

  const getCourseName = (activityId) => {
    const activity = activities.find(c => c.id === activityId);
    return activity ? activity.name : 'N/A';
  };

  const generateActiveChildrenReport = async () => {
    if (!activeReportStartDate || !activeReportEndDate) {
      toast.error('Selecione as datas de início e fim do período');
      return;
    }
    
    if (activeReportStartDate > activeReportEndDate) {
      toast.error('A data de início deve ser anterior à data de fim');
      return;
    }
    
    setActiveReportLoading(true);
    try {
      const requestData = {
        start_date: activeReportStartDate,
        end_date: activeReportEndDate
      };
      
      // Adicionar filtro de origens se selecionado
      if (activeReportOrigins.length > 0) {
        requestData.origins = activeReportOrigins;
      }
      
      const response = await axios.post(`${API}/reports/active-children-by-period`, requestData);
      setActiveChildrenReport(response.data);
      toast.success(`Relatório gerado: ${response.data.total_active_children} crianças ativas no período`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar relatório de cadastros ativos');
    } finally {
      setActiveReportLoading(false);
    }
  };

  const toggleActiveOriginFilter = (origin) => {
    setActiveReportOrigins(prev => 
      prev.includes(origin)
        ? prev.filter(o => o !== origin)
        : [...prev, origin]
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const getOriginLabel = (origin) => {
    // Padronização de nomes próprios com iniciais maiúsculas
    const labels = {
      'LIVRE': 'Livre Demanda',
      'GCR': 'Governador Celso Ramos',
      'BIG': 'Biguaçu',
      'livre_demanda': 'Livre Demanda',
      'gov_celso_ramos': 'Governador Celso Ramos',
      'biguacu': 'Biguaçu'
    };
    return labels[origin] || labels[origin?.toUpperCase()] || origin;
  };

  const exportActiveReportToCSV = () => {
    if (!activeChildrenReport || !activeChildrenReport.children.length) {
      toast.error('Nenhum dado para exportar');
      return;
    }

    const headers = ['Nome', 'Matrícula', 'Idade', 'Dt. Nascimento', 'Bairro', 'Responsável', 'Dt. Inscrição', 'Turno', 'Origem', 'Status Atual', 'Motivo Atividade', 'Atividades'];
    const rows = activeChildrenReport.children.map(child => [
      child.name,
      child.enrollment_number || '-',
      child.age || '-',
      child.birth_date ? formatDate(child.birth_date) : '-',
      child.neighborhood || '-',
      child.guardian_name || '-',
      child.created_at ? formatDate(child.created_at.split('T')[0]) : '-',
      child.shift || '-',
      getOriginLabel(child.enrollment_origin),
      child.is_currently_active ? 'Ativo' : 'Inativo',
      child.activity_reason,
      (child.activities || []).join(', ')
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `cadastros_ativos_${activeReportStartDate}_a_${activeReportEndDate}.csv`;
    link.click();
    toast.success('Relatório exportado com sucesso!');
  };

  // Função para recortar imagem em formato quadrado centralizado
  const cropImageToSquare = (imageDataUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Determinar o tamanho do quadrado (menor dimensão)
        const size = Math.min(img.width, img.height);
        canvas.width = size;
        canvas.height = size;
        
        // Calcular posição para centralizar o recorte
        const offsetX = (img.width - size) / 2;
        const offsetY = (img.height - size) / 2;
        
        // Desenhar a imagem recortada e centralizada
        ctx.drawImage(img, offsetX, offsetY, size, size, 0, 0, size, size);
        
        // Retornar como base64
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.onerror = () => resolve(imageDataUrl);
      img.src = imageDataUrl;
    });
  };

  const exportActiveReportToPDF = async () => {
    if (!activeChildrenReport || !activeChildrenReport.children.length) {
      toast.error('Nenhum dado para exportar');
      return;
    }

    toast.info('Gerando PDF...');

    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = addPdfHeader(doc);
      const photoSize = 18; // Tamanho da foto quadrada em mm
      const rowHeight = 24; // Altura de cada linha

      // === CABEÇALHO ===
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Relatório de Cadastros Ativos', pageWidth / 2, yPos, { align: 'center' });
      yPos += 8;

      // Período
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      const periodoText = `Período: ${formatDate(activeChildrenReport.period.start_date)} a ${formatDate(activeChildrenReport.period.end_date)}`;
      doc.text(periodoText, pageWidth / 2, yPos, { align: 'center' });
      yPos += 5;

      // Filtro de origem se aplicado
      if (activeChildrenReport.filters?.origins !== "Todas") {
        const originsText = Array.isArray(activeChildrenReport.filters?.origins) 
          ? activeChildrenReport.filters.origins.map(o => getOriginLabel(o)).join(', ')
          : 'Todas';
        doc.setFontSize(10);
        doc.text(`Origem: ${originsText}`, pageWidth / 2, yPos, { align: 'center' });
        yPos += 5;
      }

      // Total
      doc.setFontSize(10);
      doc.text(`Total de crianças: ${activeChildrenReport.total_active_children}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;

      // === CABEÇALHO DA TABELA ===
      const drawTableHeader = (y) => {
        doc.setFillColor(245, 245, 245);
        doc.rect(margin, y, pageWidth - margin * 2, 8, 'F');
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, y + 8, pageWidth - margin, y + 8);
        
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(60, 60, 60);
        doc.text('Foto', margin + 2, y + 5.5);
        doc.text('Nome / Matrícula', margin + photoSize + 6, y + 5.5);
        doc.text('Dt.Nasc / Bairro', margin + 72, y + 5.5);
        doc.text('Responsável', margin + 105, y + 5.5);
        doc.text('Turno', margin + 140, y + 5.5);
        doc.text('Status', margin + 158, y + 5.5);
        
        return y + 10;
      };

      yPos = drawTableHeader(yPos);
      doc.setTextColor(0, 0, 0);

      // === DADOS DAS CRIANÇAS ===
      const childrenData = activeChildrenReport.children;

      for (let i = 0; i < childrenData.length; i++) {
        const child = childrenData[i];

        // Verificar se precisa de nova página
        if (yPos + rowHeight > pageHeight - 20) {
          doc.addPage();
          yPos = addPdfHeader(doc);
          yPos = drawTableHeader(yPos);
        }

        // Linha alternada (cor de fundo)
        if (i % 2 === 0) {
          doc.setFillColor(252, 252, 252);
          doc.rect(margin, yPos, pageWidth - margin * 2, rowHeight, 'F');
        }

        // Linha divisória
        doc.setDrawColor(230, 230, 230);
        doc.line(margin, yPos + rowHeight, pageWidth - margin, yPos + rowHeight);

        // === FOTO (Quadrada) ===
        const photoX = margin + 2;
        const photoY = yPos + 2;
        
        // Borda da foto (quadrado)
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.3);
        doc.rect(photoX, photoY, photoSize, photoSize);
        
        // Tentar adicionar foto se existir
        if (child.photo_url && child.photo_url.startsWith('data:image')) {
          try {
            const croppedPhoto = await cropImageToSquare(child.photo_url);
            doc.addImage(croppedPhoto, 'JPEG', photoX + 0.5, photoY + 0.5, photoSize - 1, photoSize - 1);
          } catch (e) {
            // Se falhar, deixa o placeholder vazio
          }
        }

        // === NOME E MATRÍCULA ===
        const textX = margin + photoSize + 6;
        
        // Nome (truncar se muito longo)
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        let displayName = child.name || '-';
        if (displayName.length > 28) {
          displayName = displayName.substring(0, 25) + '...';
        }
        doc.text(displayName, textX, yPos + 7);
        
        // Matrícula e idade
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        const matriculaIdade = `Mat: ${child.enrollment_number || '-'} | ${child.age || '-'} anos`;
        doc.text(matriculaIdade, textX, yPos + 12);
        
        // Data inscrição
        const inscritoEm = child.created_at ? formatDate(child.created_at.split('T')[0]) : '-';
        doc.text(`Inscrito: ${inscritoEm}`, textX, yPos + 17);

        // === DT. NASCIMENTO / BAIRRO ===
        doc.setFontSize(8);
        const dtNasc = child.birth_date ? formatDate(child.birth_date) : '-';
        doc.text(dtNasc, margin + 72, yPos + 8);
        doc.setFontSize(7);
        let bairro = child.neighborhood || '-';
        if (bairro.length > 18) bairro = bairro.substring(0, 16) + '...';
        doc.text(bairro, margin + 72, yPos + 14);

        // === RESPONSÁVEL (com quebra de linha) ===
        doc.setFontSize(8);
        const guardianFull = child.guardian_name || '-';
        const guardianLines = doc.splitTextToSize(guardianFull, 32);
        guardianLines.slice(0, 2).forEach((line, i) => {
          doc.text(line, margin + 105, yPos + 8 + (i * 4));
        });

        // === TURNO ===
        doc.setFontSize(8);
        const turnoText = child.shift === 'Matutino' ? 'Manhã' : 'Tarde';
        doc.text(turnoText, margin + 140, yPos + 11);

        // === STATUS ===
        if (child.is_currently_active) {
          doc.setTextColor(34, 139, 34); // Verde
          doc.text('Ativo', margin + 158, yPos + 11);
        } else {
          doc.setTextColor(220, 53, 69); // Vermelho
          doc.text('Inativo', margin + 158, yPos + 11);
        }
        doc.setTextColor(0, 0, 0);

        yPos += rowHeight;
      }

      // === RODAPÉ ===
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Página ${i} de ${totalPages} - Gerado em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      }

      doc.save(`cadastros_ativos_${activeReportStartDate}_a_${activeReportEndDate}.pdf`);
      toast.success('PDF exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar PDF');
    }
  };

  // === FUNÇÕES DO RELATÓRIO DE ANIVERSARIANTES ===
  
  // Função para converter days_of_week para o padrão correspondente
  const getDayPatternFromDaysOfWeek = (daysOfWeek) => {
    if (!daysOfWeek || !Array.isArray(daysOfWeek)) return 'seg_sex';
    
    const days = daysOfWeek.map(d => d.toLowerCase()).sort();
    const daysStr = days.join(',');
    
    // Verificar padrões conhecidos
    // Ter e Qui: ['terca', 'quinta'] ou ['quinta', 'terca']
    if (days.length === 2 && days.includes('terca') && days.includes('quinta')) {
      return 'ter_qui';
    }
    
    // Qua e Sex: ['quarta', 'sexta'] ou ['sexta', 'quarta']
    if (days.length === 2 && days.includes('quarta') && days.includes('sexta')) {
      return 'qua_sex';
    }
    
    // Seg a Sex: todos os 5 dias
    if (days.length >= 5 || 
        (days.includes('segunda') && days.includes('terca') && days.includes('quarta') && 
         days.includes('quinta') && days.includes('sexta'))) {
      return 'seg_sex';
    }
    
    // Se não corresponder a nenhum padrão específico, considerar como seg_sex
    return 'seg_sex';
  };

  // === FUNÇÕES DO RELATÓRIO DE LISTA DE CRIANÇAS ===
  
  const generateChildListReport = async () => {
    if (childListColumns.length === 0) {
      toast.error('Selecione pelo menos uma coluna para exibir');
      return;
    }

    setChildListLoading(true);
    try {
      // Buscar crianças com base no status selecionado
      const activeOnly = childListStatus === 'active' ? 'true' : 'false';
      const response = await axios.get(`${API}/children?active_only=${activeOnly}`);
      
      // Filtrar por status se "both" não estiver selecionado (o backend já filtra se active_only=true)
      let allChildren = response.data;
      if (childListStatus === 'inactive') {
        // Se pediu apenas inativos, filtrar os que não são ativos
        allChildren = allChildren.filter(c => !c.is_active);
      }
      
      // Aplicar filtros
      let filtered = allChildren.filter(child => {
        // Filtro por origem
        if (childListOrigins.length > 0 && childListOrigins.length < 3) {
          if (!childListOrigins.includes(child.enrollment_origin)) return false;
        }
        
        // Filtro por turno
        if (childListShifts.length > 0 && childListShifts.length < 2) {
          if (!childListShifts.includes(child.shift)) return false;
        }
        
        // Filtro por dias
        if (childListDays.length > 0 && childListDays.length < 3) {
          const childPattern = getDayPatternFromDaysOfWeek(child.days_of_week);
          if (!childListDays.includes(childPattern)) return false;
        }
        
        // Filtro por gênero
        if (childListGenders.length > 0 && childListGenders.length < 2) {
          if (!childListGenders.includes(child.gender?.toUpperCase())) return false;
        }
        
        // Filtro por idade
        if (childListAges.length > 0) {
          if (!childListAges.includes(child.age)) return false;
        }
        
        return true;
      });
      
      // Ordenação
      filtered.sort((a, b) => {
        let valA, valB;
        
        switch (childListSortBy) {
          case 'name':
            valA = a.name || '';
            valB = b.name || '';
            return childListSortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
          case 'age':
            valA = a.age || 0;
            valB = b.age || 0;
            return childListSortOrder === 'asc' ? valA - valB : valB - valA;
          case 'enrollment_number':
            valA = parseInt((a.enrollment_number || '0').toString().replace(/\D/g, ''), 10) || 0;
            valB = parseInt((b.enrollment_number || '0').toString().replace(/\D/g, ''), 10) || 0;
            return childListSortOrder === 'asc' ? valA - valB : valB - valA;
          case 'shift':
            valA = a.shift || '';
            valB = b.shift || '';
            return childListSortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
          case 'days_of_week':
            valA = getDayPatternFromDaysOfWeek(a.days_of_week);
            valB = getDayPatternFromDaysOfWeek(b.days_of_week);
            return childListSortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
          case 'gender':
            valA = a.gender || '';
            valB = b.gender || '';
            return childListSortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
          case 'enrollment_origin':
            valA = a.enrollment_origin || '';
            valB = b.enrollment_origin || '';
            return childListSortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
          default:
            return 0;
        }
      });
      
      setChildListReport({
        children: filtered,
        columns: childListColumns,
        filters: {
          origins: childListOrigins,
          shifts: childListShifts,
          days: childListDays,
          genders: childListGenders,
          ages: childListAges
        },
        sortBy: childListSortBy,
        sortOrder: childListSortOrder
      });
      
      toast.success(`${filtered.length} crianças encontradas`);
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      toast.error('Erro ao gerar relatório');
    } finally {
      setChildListLoading(false);
    }
  };

  // Função para formatar valor de coluna para exibição
  const formatChildListValue = (child, column) => {
    switch (column) {
      case 'enrollment_number':
        return child.enrollment_number || '-';
      case 'name':
        return child.name || '-';
      case 'shift':
        return child.shift === 'Matutino' ? 'Manhã' : 'Tarde';
      case 'days_of_week':
        const pattern = getDayPatternFromDaysOfWeek(child.days_of_week);
        return pattern === 'ter_qui' ? 'Ter e Qui' : pattern === 'qua_sex' ? 'Qua e Sex' : 'Seg a Sex';
      case 'gender':
        return child.gender === 'MASCULINO' ? 'M' : 'F';
      case 'birth_date':
        if (!child.birth_date) return '-';
        return new Date(child.birth_date + 'T12:00:00').toLocaleDateString('pt-BR');
      case 'age':
        return child.age ? `${child.age}` : '-';
      case 'guardian_phone':
        return child.guardian_phone || '-';
      case 'guardian_name':
        return child.guardian_name || '-';
      case 'guardian_relationship':
        return child.guardian_relationship || '-';
      case 'enrollment_origin':
        const originLabels = {
          'livre_demanda': 'Livre Demanda',
          'gov_celso_ramos': 'Gov. Celso Ramos',
          'biguacu': 'Biguacu'
        };
        return originLabels[child.enrollment_origin] || child.enrollment_origin || '-';
      case 'neighborhood':
        return child.neighborhood || '-';
      default:
        return '-';
    }
  };

  // Função para exportar lista de crianças para PDF
  const exportChildListPDF = async () => {
    if (!childListReport || childListReport.children.length === 0) {
      toast.error('Nenhum dado para exportar');
      return;
    }

    try {
      const doc = new jsPDF('l', 'mm', 'a4'); // Landscape para caber mais colunas
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 10;
      
      // Usar as colunas salvas no relatório
      const reportColumns = childListReport.columns || childListColumns;
      
      // Logo
      const startY = addPdfHeader(doc);
      
      // Título
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text('Lista de Criancas', pageWidth / 2, startY, { align: 'center' });
      
      // Subtítulo com total
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Total: ${childListReport.children.length} criancas`, pageWidth / 2, startY + 7, { align: 'center' });
      
      // Função para remover acentos
      const removeAccents = (str) => {
        if (!str) return '-';
        return String(str)
          .replace(/[ãâáàä]/gi, 'a')
          .replace(/[êéèë]/gi, 'e')
          .replace(/[íìïî]/gi, 'i')
          .replace(/[õôóòö]/gi, 'o')
          .replace(/[úùüû]/gi, 'u')
          .replace(/[ç]/gi, 'c')
          .replace(/[ñ]/gi, 'n');
      };
      
      // Preparar dados da tabela
      const headers = reportColumns.map(col => {
        const option = childListColumnOptions.find(o => o.value === col);
        return removeAccents(option ? option.label : col);
      });
      
      const rows = childListReport.children.map(child => 
        reportColumns.map(col => removeAccents(formatChildListValue(child, col)))
      );
      
      // Gerar tabela
      const headerH = getPdfHeaderHeight(doc);
      autoTable(doc, {
        head: [headers],
        body: rows,
        startY: startY + 12,
        margin: { left: margin, right: margin, top: headerH },
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        didDrawPage: (data) => {
          // Adicionar logo em todas as páginas exceto a primeira (já tem)
          if (data.pageNumber > 1) {
            addPdfHeader(doc);
          }
        }
      });
      
      // Rodapé em todas as páginas
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128);
        doc.text(
          `Gerado em ${new Date().toLocaleDateString('pt-BR')} - Pagina ${i} de ${pageCount}`,
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
      }
      
      doc.save(`lista_criancas_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar PDF');
    }
  };
  
  const generateBirthdayReport = async () => {
    if (!birthdayStartDate || !birthdayEndDate) {
      toast.error('Selecione o período de datas');
      return;
    }

    setBirthdayLoading(true);
    try {
      const response = await axios.get(`${API}/children`);
      const allChildren = response.data.filter(c => c.is_active);
      
      // Filtrar por período de aniversário
      const startParts = birthdayStartDate.split('-');
      const endParts = birthdayEndDate.split('-');
      const startMonth = parseInt(startParts[1]);
      const startDay = parseInt(startParts[2]);
      const endMonth = parseInt(endParts[1]);
      const endDay = parseInt(endParts[2]);
      
      const filteredChildren = allChildren.filter(child => {
        // Filtrar por origem
        if (birthdayOrigins.length > 0 && !birthdayOrigins.includes(child.enrollment_origin)) {
          return false;
        }
        
        // Filtrar por turno
        if (birthdayShifts.length > 0 && !birthdayShifts.includes(child.shift)) {
          return false;
        }
        
        // Filtrar por padrão de dias da semana
        // Converter days_of_week para o padrão correspondente
        if (birthdayDayPatterns.length > 0 && birthdayDayPatterns.length < 3) {
          const childPattern = getDayPatternFromDaysOfWeek(child.days_of_week);
          if (!birthdayDayPatterns.includes(childPattern)) {
            return false;
          }
        }
        
        // Filtrar por período de aniversário
        if (!child.birth_date) return false;
        
        const birthParts = child.birth_date.split('-');
        const birthMonth = parseInt(birthParts[1]);
        const birthDay = parseInt(birthParts[2]);
        
        // Verificar se o aniversário está no período
        if (startMonth === endMonth) {
          // Mesmo mês
          return birthMonth === startMonth && birthDay >= startDay && birthDay <= endDay;
        } else if (startMonth < endMonth) {
          // Período não cruza ano
          if (birthMonth === startMonth) {
            return birthDay >= startDay;
          } else if (birthMonth === endMonth) {
            return birthDay <= endDay;
          } else {
            return birthMonth > startMonth && birthMonth < endMonth;
          }
        } else {
          // Período cruza ano (ex: Nov a Fev)
          if (birthMonth >= startMonth) {
            return birthMonth === startMonth ? birthDay >= startDay : true;
          } else if (birthMonth <= endMonth) {
            return birthMonth === endMonth ? birthDay <= endDay : true;
          }
          return false;
        }
      });
      
      // Ordenar por dia de aniversário
      const sortedChildren = filteredChildren.sort((a, b) => {
        const aMonth = parseInt(a.birth_date.split('-')[1]);
        const aDay = parseInt(a.birth_date.split('-')[2]);
        const bMonth = parseInt(b.birth_date.split('-')[1]);
        const bDay = parseInt(b.birth_date.split('-')[2]);
        
        if (aMonth !== bMonth) return aMonth - bMonth;
        return aDay - bDay;
      });
      
      setBirthdayReport({
        children: sortedChildren,
        filters: {
          startDate: birthdayStartDate,
          endDate: birthdayEndDate,
          origins: birthdayOrigins,
          shifts: birthdayShifts,
          dayPatterns: birthdayDayPatterns
        }
      });
      
      toast.success(`${sortedChildren.length} aniversariantes encontrados!`);
    } catch (error) {
      toast.error('Erro ao gerar relatório');
    } finally {
      setBirthdayLoading(false);
    }
  };

  const formatBirthdayDate = (dateStr) => {
    if (!dateStr) return '';
    const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    const parts = dateStr.split('-');
    const day = parseInt(parts[2]);
    const month = parseInt(parts[1]) - 1;
    return `${day} de ${months[month]}`;
  };

  // Versão para PDF sem caracteres especiais
  const formatBirthdayDatePDF = (dateStr) => {
    if (!dateStr) return '';
    const months = ['Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho', 
                    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    const parts = dateStr.split('-');
    const day = parseInt(parts[2]);
    const month = parseInt(parts[1]) - 1;
    return `${day} de ${months[month]}`;
  };

  const exportBirthdayReportPDF = async () => {
    if (!birthdayReport || birthdayReport.children.length === 0) {
      toast.error('Nenhum dado para exportar');
      return;
    }

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 14;
      let yPos = addPdfHeader(doc);

      // === CABEÇALHO ===
      doc.setFillColor(216, 122, 78); // Cor tema
      doc.rect(0, yPos - 2, pageWidth, 35, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text('Relatório de Aniversariantes', pageWidth / 2, yPos + 8, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      const startFormatted = new Date(birthdayReport.filters.startDate + 'T12:00:00').toLocaleDateString('pt-BR');
      const endFormatted = new Date(birthdayReport.filters.endDate + 'T12:00:00').toLocaleDateString('pt-BR');
      doc.text(`Período: ${startFormatted} a ${endFormatted}`, pageWidth / 2, yPos + 16, { align: 'center' });
      
      // Filtros aplicados
      const origensLabels = birthdayReport.filters.origins.map(o => {
        const found = origins.find(x => x.value === o);
        return found ? found.label : o;
      }).join(', ');
      const turnosLabels = birthdayReport.filters.shifts.map(s => s === 'Matutino' ? 'Manha' : 'Tarde').join(', ');
      const diasLabels = birthdayReport.filters.dayPatterns.map(p => {
        const found = dayPatterns.find(x => x.value === p);
        return found ? found.label : p;
      }).join(', ');
      doc.text(`Origem: ${origensLabels || 'Todas'} | Turno: ${turnosLabels || 'Todos'} | Dias: ${diasLabels || 'Todos'}`, pageWidth / 2, yPos + 23, { align: 'center' });

      yPos = yPos + 40;
      doc.setTextColor(0, 0, 0);

      // === RESUMO ===
      doc.setFillColor(240, 240, 240);
      doc.rect(margin, yPos, pageWidth - (margin * 2), 12, 'F');
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text(`Total de Aniversariantes: ${birthdayReport.children.length}`, margin + 5, yPos + 8);
      yPos += 18;

      // === LISTA DE ANIVERSARIANTES ===
      const rowHeight = 22;
      
      for (const child of birthdayReport.children) {
        // Verificar quebra de página
        if (yPos + rowHeight > pageHeight - 20) {
          doc.addPage();
          yPos = addPdfHeader(doc);
        }

        // Card do aniversariante
        doc.setFillColor(250, 250, 250);
        doc.roundedRect(margin, yPos, pageWidth - (margin * 2), rowHeight - 2, 2, 2, 'F');
        doc.setDrawColor(220, 220, 220);
        doc.roundedRect(margin, yPos, pageWidth - (margin * 2), rowHeight - 2, 2, 2, 'S');

        // Foto placeholder (círculo)
        doc.setFillColor(216, 122, 78);
        doc.circle(margin + 10, yPos + (rowHeight - 2) / 2, 6, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.setFont(undefined, 'bold');
        const initials = child.name ? child.name.charAt(0).toUpperCase() : '?';
        doc.text(initials, margin + 10, yPos + (rowHeight - 2) / 2 + 2, { align: 'center' });

        // Nome
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text(child.name || 'Sem nome', margin + 22, yPos + 7);

        // Data de aniversário e idade
        doc.setFont(undefined, 'normal');
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        const birthdayText = formatBirthdayDatePDF(child.birth_date);
        const ageText = child.age ? `${child.age} anos` : '';
        doc.text(`${birthdayText} - ${ageText}`, margin + 22, yPos + 14);

        // Turno e Origem (lado direito)
        doc.setFontSize(8);
        const turnoText = child.shift === 'Matutino' ? 'Manha' : 'Tarde';
        const origemText = getOriginLabel(child.enrollment_origin);
        doc.text(`${turnoText} - ${origemText}`, pageWidth - margin - 5, yPos + 10, { align: 'right' });

        yPos += rowHeight;
      }

      // === RODAPÉ ===
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Página ${i} de ${totalPages} - Gerado em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      }

      doc.save(`aniversariantes_${birthdayStartDate}_a_${birthdayEndDate}.pdf`);
      toast.success('PDF exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar PDF');
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-800">Relatórios</h1>
        <p className="text-slate-600 mt-1 text-sm sm:text-base">Gerencie relatórios e ocorrências</p>
      </div>

      <Tabs defaultValue="child" className="w-full">
        <TabsList className="flex w-full overflow-x-auto gap-1 sm:grid sm:grid-cols-7 sm:gap-0">
          <TabsTrigger value="child" data-testid="tab-child-reports" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">Crianças</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-course-reports" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">Atividades</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="tab-monthly-reports" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">Mensal</TabsTrigger>
          <TabsTrigger value="active-children" data-testid="tab-active-children" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">Cadastros Ativos</TabsTrigger>
          <TabsTrigger value="birthdays" data-testid="tab-birthdays" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">Aniversariantes</TabsTrigger>
          <TabsTrigger value="child-list" data-testid="tab-child-list" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">Lista Crianças</TabsTrigger>
          <TabsTrigger value="relatos" data-testid="tab-relatos" className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0">Relatos</TabsTrigger>
        </TabsList>

        {/* Child Reports */}
        <TabsContent value="child" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetChildReportForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-child-report-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Novo Relatório
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Novo Relatório de Criança</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleChildReportSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="child_id">Criança *</Label>
                    <select
                      id="child_id"
                      data-testid="report-child-select"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={childReportForm.child_id}
                      onChange={(e) => setChildReportForm({ ...childReportForm, child_id: e.target.value })}
                      required
                    >
                      <option value="">Selecione uma criança</option>
                      {children.map(child => (
                        <option key={child.id} value={child.id}>{child.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="report_type">Tipo *</Label>
                      <select
                        id="report_type"
                        data-testid="report-type-select"
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={childReportForm.report_type}
                        onChange={(e) => setChildReportForm({ ...childReportForm, report_type: e.target.value })}
                      >
                        <option value="comportamental">Comportamental</option>
                        <option value="ocorrencia">Ocorrência</option>
                        <option value="geral">Geral</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="severity">Gravidade *</Label>
                      <select
                        id="severity"
                        data-testid="report-severity-select"
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={childReportForm.severity}
                        onChange={(e) => setChildReportForm({ ...childReportForm, severity: e.target.value })}
                      >
                        <option value="baixa">Baixa</option>
                        <option value="media">Média</option>
                        <option value="alta">Alta</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="date">Data *</Label>
                    <Input
                      id="date"
                      type="date"
                      data-testid="report-date-input"
                      value={childReportForm.date}
                      onChange={(e) => setChildReportForm({ ...childReportForm, date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="title">Título *</Label>
                    <Input
                      id="title"
                      data-testid="report-title-input"
                      value={childReportForm.title}
                      onChange={(e) => setChildReportForm({ ...childReportForm, title: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Descrição *</Label>
                    <textarea
                      id="description"
                      data-testid="report-description-textarea"
                      className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={childReportForm.description}
                      onChange={(e) => setChildReportForm({ ...childReportForm, description: e.target.value })}
                      required
                    />
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" data-testid="save-child-report-button">
                      Salvar
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-3" data-testid="child-reports-list">
            {childReports.map(report => (
              <div key={report.id} className="bg-white rounded-xl shadow-md p-6 border border-slate-100">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-slate-800">{report.title}</h3>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        report.severity === 'alta' ? 'bg-red-100 text-red-700' :
                        report.severity === 'media' ? 'bg-orange-100 text-orange-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {report.severity}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mb-2">{report.description}</p>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span>Criança: <strong>{getChildName(report.child_id)}</strong></span>
                      <span>Tipo: <strong>{report.report_type}</strong></span>
                      <span>Data: <strong>{new Date(report.date).toLocaleDateString('pt-BR')}</strong></span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => handleDeleteChildReport(report.id)}
                    data-testid={`delete-child-report-${report.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            {childReports.length === 0 && (
              <div className="bg-white rounded-xl shadow-md p-12 text-center border border-slate-100">
                <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">Nenhum relatório de criança cadastrado</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Course Reports */}
        <TabsContent value="activity" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={activityDialogOpen} onOpenChange={(open) => { setCourseDialogOpen(open); if (!open) resetCourseReportForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-activity-report-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Novo Relatório de atividade
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Novo Relatório de atividade</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCourseReportSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="activity_id">atividade *</Label>
                    <select
                      id="activity_id"
                      data-testid="activity-report-activity-select"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={activityReportForm.activity_id}
                      onChange={(e) => setCourseReportForm({ ...activityReportForm, activity_id: e.target.value })}
                      required
                    >
                      <option value="">Selecione um atividade</option>
                      {activities.map(activity => (
                        <option key={activity.id} value={activity.id}>{activity.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="activity_date">Data *</Label>
                    <Input
                      id="activity_date"
                      type="date"
                      data-testid="activity-report-date-input"
                      value={activityReportForm.date}
                      onChange={(e) => setCourseReportForm({ ...activityReportForm, date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="activity_title">Título *</Label>
                    <Input
                      id="activity_title"
                      data-testid="activity-report-title-input"
                      value={activityReportForm.title}
                      onChange={(e) => setCourseReportForm({ ...activityReportForm, title: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="activity_description">Descrição *</Label>
                    <textarea
                      id="activity_description"
                      data-testid="activity-report-description-textarea"
                      className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={activityReportForm.description}
                      onChange={(e) => setCourseReportForm({ ...activityReportForm, description: e.target.value })}
                      required
                    />
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={() => setCourseDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" data-testid="save-activity-report-button">
                      Salvar
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-3" data-testid="course-reports-list">
            {activityReports.map(report => (
              <div key={report.id} className="bg-white rounded-xl shadow-md p-6 border border-slate-100">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-800 mb-2">{report.title}</h3>
                    <p className="text-sm text-slate-600 mb-2">{report.description}</p>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span>atividade: <strong>{getCourseName(report.activity_id)}</strong></span>
                      <span>Data: <strong>{new Date(report.date).toLocaleDateString('pt-BR')}</strong></span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => handleDeleteCourseReport(report.id)}
                    data-testid={`delete-activity-report-${report.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            {activityReports.length === 0 && (
              <div className="bg-white rounded-xl shadow-md p-12 text-center border border-slate-100">
                <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">Nenhum relatório de atividade cadastrado</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Monthly Report */}
        <TabsContent value="monthly" className="space-y-4">
          {/* Tipo de Relatório */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-slate-100">
            <Label className="text-sm font-medium text-slate-700 mb-3 block">Tipo de Relatório</Label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setReportType('group')}
                className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  reportType === 'group'
                    ? 'bg-blue-500 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <TrendingUp className="h-4 w-4 inline mr-2" />
                Relatório de Grupo
              </button>
              <button
                type="button"
                onClick={() => setReportType('individual')}
                className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  reportType === 'individual'
                    ? 'bg-purple-500 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <User className="h-4 w-4 inline mr-2" />
                Relatório Individual
              </button>
            </div>
          </div>

          {/* Filtros (apenas para relatório de grupo) */}
          {reportType === 'group' && (
            <div className="bg-white rounded-xl shadow-md p-6 border border-slate-100">
              <div className="flex items-center gap-2 mb-4">
                <Filter className="h-5 w-5 text-slate-600" />
                <h3 className="font-semibold text-slate-800">Filtros do Relatório</h3>
              </div>

              {/* Origem */}
              <div className="mb-4">
                <Label className="text-sm font-medium text-slate-700 mb-2">Origem</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectAllOrigins}
                    className="text-xs"
                  >
                    Todas
                  </Button>
                  {origins.map(origin => (
                    <button
                      key={origin.value}
                      type="button"
                      onClick={() => toggleOriginFilter(origin.value)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        selectedOrigins.includes(origin.value)
                          ? 'bg-blue-500 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {origin.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Turno */}
              <div className="mb-4">
                <Label className="text-sm font-medium text-slate-700 mb-2">Turno</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectAllShifts}
                    className="text-xs"
                  >
                    Todos
                  </Button>
                  {shifts.map(shift => (
                    <button
                      key={shift.value}
                      type="button"
                      onClick={() => toggleShiftFilter(shift.value)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        selectedShifts.includes(shift.value)
                          ? 'bg-green-500 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {shift.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Dias */}
              <div className="mb-4">
                <Label className="text-sm font-medium text-slate-700 mb-2">Dias de Participação</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectAllDays}
                    className="text-xs"
                  >
                    Todos
                  </Button>
                  {dayPatterns.map(day => (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => toggleDaysFilter(day.value)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        selectedDays.includes(day.value)
                          ? 'bg-purple-500 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Idade */}
              <div className="mb-4">
                <Label className="text-sm font-medium text-slate-700 mb-2">Faixa Etária</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectAllAges}
                    className="text-xs"
                  >
                    Todas
                  </Button>
                  {ageRanges.map(age => (
                    <button
                      key={age.value}
                      type="button"
                      onClick={() => toggleAgeFilter(age.value)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        selectedAges.includes(age.value)
                          ? 'bg-orange-500 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {age.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Filtro por Atividade */}
              <div className="mb-4">
                <Label className="text-sm font-medium text-slate-700 mb-2">Atividade</Label>
                <select
                  value={selectedActivityId}
                  onChange={(e) => setSelectedActivityId(e.target.value)}
                  className="w-full mt-2 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
                  data-testid="filter-activity"
                >
                  <option value="">Todas as atividades</option>
                  {allActivities.map(act => (
                    <option key={act.id} value={act.id}>{act.name}</option>
                  ))}
                </select>
              </div>

              {/* Filtro por Dia da Semana */}
              <div className="mb-4">
                <Label className="text-sm font-medium text-slate-700 mb-2">Dia da Semana</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  <button type="button" onClick={() => setSelectedDaysOfWeek([])}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedDaysOfWeek.length === 0 ? 'bg-indigo-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
                    Todos
                  </button>
                  {[{value:'segunda',label:'Seg'},{value:'terca',label:'Ter'},{value:'quarta',label:'Qua'},{value:'quinta',label:'Qui'},{value:'sexta',label:'Sex'}].map(d => (
                    <button key={d.value} type="button" onClick={() => setSelectedDaysOfWeek(prev => prev.includes(d.value) ? prev.filter(v => v !== d.value) : [...prev, d.value])}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedDaysOfWeek.includes(d.value) ? 'bg-indigo-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Filtro por Horário */}
              <div className="mb-4">
                <Label className="text-sm font-medium text-slate-700 mb-2">Horário</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  <button type="button" onClick={() => setSelectedTimeSlots([])}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedTimeSlots.length === 0 ? 'bg-teal-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
                    Todos
                  </button>
                  {[{value:'1',label:'1° Horário'},{value:'2',label:'2° Horário'},{value:'3',label:'3° Horário'}].map(s => (
                    <button key={s.value} type="button" onClick={() => setSelectedTimeSlots(prev => prev.includes(s.value) ? prev.filter(v => v !== s.value) : [...prev, s.value])}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedTimeSlots.includes(s.value) ? 'bg-teal-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Seleção de Criança (apenas para relatório individual) */}
          {reportType === 'individual' && (
            <div className="bg-white rounded-xl shadow-md p-6 border border-slate-100">
              <Label htmlFor="child_search">Selecione a Criança</Label>
              <Input
                id="child_search"
                type="text"
                placeholder="Digite o nome para buscar..."
                className="mt-2 mb-2"
                value={childSearchTerm}
                onChange={(e) => setChildSearchTerm(e.target.value)}
              />
              <div 
                id="children-list"
                className="max-h-64 overflow-y-auto border border-slate-200 rounded-md"
              >
                {children
                  .slice()
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .filter(child => 
                    !childSearchTerm || 
                    (child.name || '').toLowerCase().startsWith(childSearchTerm.toLowerCase())
                  )
                  .map(child => (
                    <div
                      key={child.id}
                      onClick={() => {
                        setSelectedChild(child.id);
                        setChildSearchTerm(child.name);
                      }}
                      className={`px-4 py-3 cursor-pointer border-b border-slate-100 last:border-b-0 transition-colors ${
                        selectedChild === child.id
                          ? 'bg-[#D87A4E]/20 text-[#7A3D36] font-medium'
                          : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="font-medium">{child.name}</div>
                      <div className="text-xs text-slate-500">
                        {child.age} anos • {child.enrollment_number || 'Sem matrícula'}
                      </div>
                    </div>
                  ))
                }
                {children.filter(child => 
                  !childSearchTerm || 
                  (child.name || '').toLowerCase().startsWith(childSearchTerm.toLowerCase())
                ).length === 0 && (
                  <div className="px-4 py-6 text-center text-slate-500">
                    Nenhuma criança encontrada
                  </div>
                )}
              </div>
              {selectedChild && (
                <div className="mt-3 p-3 bg-[#D87A4E]/10 rounded-lg flex items-center justify-between">
                  <span className="text-sm font-medium text-[#7A3D36]">
                    Selecionado: {children.find(c => c.id === selectedChild)?.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedChild('');
                      setChildSearchTerm('');
                    }}
                    className="text-xs text-[#7A3D36] hover:underline"
                  >
                    Limpar
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Período do Relatório */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-slate-100">
            <Label className="text-sm font-medium text-slate-700 mb-3 block">Período do Relatório</Label>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <Label htmlFor="start_date" className="text-xs">Data Início</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="end_date" className="text-xs">Data Fim</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-500">OU</span>
              </div>
            </div>

            <div>
              <Label htmlFor="report_month" className="text-xs">Selecione o Mês</Label>
              <Input
                id="report_month"
                type="month"
                data-testid="monthly-report-month-input"
                value={reportMonth}
                onChange={(e) => setReportMonth(e.target.value)}
                className="mt-1"
              />
            </div>

            <div className="mt-4">
              <Button 
                onClick={reportType === 'group' ? generateMonthlyReport : generateIndividualReport}
                data-testid="generate-report-button"
                className="w-full"
              >
                <TrendingUp className="h-4 w-4 mr-2" />
                Gerar Relatório
              </Button>
            </div>
          </div>

          {/* Relatório - Grade */}
          {sortedGridReport && (
            <div data-testid="grid-report-result">
              <div className="bg-white rounded-xl shadow-md p-6 border border-slate-100 mb-6">
                <h3 className="text-xl font-semibold text-slate-800 mb-2">
                  {reportType === 'individual' ? 'Relatório Individual' : 'Relatório de Frequência'} - {sortedGridReport.period}
                </h3>
                {sortedGridReport.activity && (
                  <p className="text-sm text-indigo-600 font-medium mb-1">
                    Atividade: {sortedGridReport.activity.name}
                    {selectedDaysOfWeek.length > 0 && ` • ${selectedDaysOfWeek.map(d => ({segunda:'Seg',terca:'Ter',quarta:'Qua',quinta:'Qui',sexta:'Sex'})[d] || d).join(', ')}`}
                    {selectedTimeSlots.length > 0 && ` • ${selectedTimeSlots.map(s => `${s}° Hor`).join(', ')}`}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-4 mb-4">
                  <p className="text-slate-600">
                    Total de crianças: <strong>{sortedGridReport.total_children}</strong>
                  </p>
                  {sortedGridReport.avg_attendance_rate !== undefined && sortedGridReport.total_children > 0 && (
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold ${
                      sortedGridReport.avg_attendance_rate >= 80 ? 'bg-green-100 text-green-700' :
                      sortedGridReport.avg_attendance_rate >= 60 ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      <TrendingUp className="h-4 w-4" />
                      Média de presença da turma: {sortedGridReport.avg_attendance_rate}%
                    </div>
                  )}
                </div>
                
                {/* Controles de Ordenação */}
                {reportType === 'group' && sortedGridReport.total_children > 1 && (
                  <div className="flex flex-wrap gap-4 items-end pt-4 border-t border-slate-200">
                    <div className="flex-1 min-w-[200px]">
                      <Label htmlFor="sort-by" className="text-xs font-medium text-slate-700">Ordenar por</Label>
                      <select
                        id="sort-by"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="name">Nome</option>
                        <option value="age">Idade</option>
                        <option value="attendance_rate">Índice de Presença</option>
                      </select>
                    </div>
                    
                    <div className="flex-1 min-w-[200px]">
                      <Label htmlFor="sort-order" className="text-xs font-medium text-slate-700">Ordem</Label>
                      <select
                        id="sort-order"
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value)}
                        className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="asc">Crescente</option>
                        <option value="desc">Decrescente</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
              <AttendanceGridReport 
                gridData={sortedGridReport} 
                reportType={reportType}
              />
            </div>
          )}
        </TabsContent>

        {/* Active Children Report - Cadastros Ativos */}
        <TabsContent value="active-children" className="space-y-4">
          <div className="bg-white rounded-xl shadow-md p-6 border border-slate-100">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="h-5 w-5 text-slate-600" />
              <h3 className="font-semibold text-slate-800">Relatório de Cadastros Ativos por Período</h3>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Este relatório lista todas as crianças que tiveram vínculo ativo (cadastro ou matrícula) 
              dentro do período selecionado, independentemente do status atual.
            </p>
            
            {/* Filtro de Origem */}
            <div className="mb-4">
              <Label className="text-sm font-medium text-slate-700 mb-2 block">Filtrar por Origem (opcional)</Label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setActiveReportOrigins([])}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activeReportOrigins.length === 0
                      ? 'bg-blue-500 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  data-testid="active-report-origin-all"
                >
                  Todas
                </button>
                <button
                  type="button"
                  onClick={() => toggleActiveOriginFilter('livre_demanda')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activeReportOrigins.includes('livre_demanda')
                      ? 'bg-green-500 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  data-testid="active-report-origin-livre"
                >
                  Livre Demanda
                </button>
                <button
                  type="button"
                  onClick={() => toggleActiveOriginFilter('gov_celso_ramos')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activeReportOrigins.includes('gov_celso_ramos')
                      ? 'bg-purple-500 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  data-testid="active-report-origin-gcr"
                >
                  Governador Celso Ramos
                </button>
                <button
                  type="button"
                  onClick={() => toggleActiveOriginFilter('biguacu')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activeReportOrigins.includes('biguacu')
                      ? 'bg-orange-500 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  data-testid="active-report-origin-big"
                >
                  Biguaçu
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <Label htmlFor="active_start_date" className="text-sm">Data Início *</Label>
                <Input
                  id="active_start_date"
                  type="date"
                  data-testid="active-report-start-date"
                  value={activeReportStartDate}
                  onChange={(e) => setActiveReportStartDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="active_end_date" className="text-sm">Data Fim *</Label>
                <Input
                  id="active_end_date"
                  type="date"
                  data-testid="active-report-end-date"
                  value={activeReportEndDate}
                  onChange={(e) => setActiveReportEndDate(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            <Button 
              onClick={generateActiveChildrenReport}
              data-testid="generate-active-report-button"
              className="w-full"
              disabled={activeReportLoading}
            >
              {activeReportLoading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                  Gerando...
                </span>
              ) : (
                <>
                  <Users className="h-4 w-4 mr-2" />
                  Gerar Relatório de Cadastros Ativos
                </>
              )}
            </Button>
          </div>

          {/* Resultado do Relatório */}
          {activeChildrenReport && (
            <div className="space-y-4" data-testid="active-children-report-result">
              {/* Resumo */}
              <div className="bg-white rounded-xl shadow-md p-6 border border-slate-100">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-800">
                      Período: {formatDate(activeChildrenReport.period.start_date)} a {formatDate(activeChildrenReport.period.end_date)}
                    </h3>
                    {activeChildrenReport.filters?.origins !== "Todas" && (
                      <p className="text-sm text-slate-500 mt-1">
                        Filtrado por: {Array.isArray(activeChildrenReport.filters?.origins) 
                          ? activeChildrenReport.filters.origins.map(o => getOriginLabel(o)).join(', ')
                          : activeChildrenReport.filters?.origins}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={exportActiveReportToCSV}
                      data-testid="export-active-report-csv"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      <span className="hidden sm:inline">Exportar </span>CSV
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={exportActiveReportToPDF}
                      data-testid="export-active-report-pdf"
                      className="bg-red-50 hover:bg-red-100 border-red-200"
                    >
                      <FileDown className="h-4 w-4 mr-2 text-red-600" />
                      <span className="hidden sm:inline">Exportar </span>PDF
                    </Button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-4">
                  <div className="bg-blue-50 rounded-lg p-2 sm:p-4 text-center">
                    <div className="text-lg sm:text-2xl font-bold text-blue-700">{activeChildrenReport.total_active_children}</div>
                    <div className="text-xs text-blue-600">Total no Período</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-2 sm:p-4 text-center">
                    <div className="text-lg sm:text-2xl font-bold text-green-700">{activeChildrenReport.summary?.currently_active || 0}</div>
                    <div className="text-xs text-green-600">Atualmente Ativos</div>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-2 sm:p-4 text-center">
                    <div className="text-lg sm:text-2xl font-bold text-orange-700">{activeChildrenReport.summary?.currently_inactive || 0}</div>
                    <div className="text-xs text-orange-600">Atualmente Inativos</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-2 sm:p-4 text-center">
                    <div className="text-lg sm:text-2xl font-bold text-purple-700">
                      {(activeChildrenReport.summary?.by_shift?.Matutino || 0)} / {(activeChildrenReport.summary?.by_shift?.Vespertino || 0)}
                    </div>
                    <div className="text-xs text-purple-600">Manhã / Tarde</div>
                  </div>
                </div>

                {/* Resumo por Origem */}
                <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-200">
                  <span className="text-xs sm:text-sm text-slate-600 mr-2">Por origem:</span>
                  <span className="px-2 py-1 bg-slate-100 rounded text-xs">
                    Livre Demanda: {activeChildrenReport.summary?.by_origin?.LIVRE || 0}
                  </span>
                  <span className="px-2 py-1 bg-slate-100 rounded text-xs">
                    Governador Celso Ramos: {activeChildrenReport.summary?.by_origin?.GCR || 0}
                  </span>
                  <span className="px-2 py-1 bg-slate-100 rounded text-xs">
                    Biguaçu: {activeChildrenReport.summary?.by_origin?.BIG || 0}
                  </span>
                </div>
              </div>

              {/* Lista de Crianças - Desktop Table */}
              <div className="bg-white rounded-xl shadow-md border border-slate-100 overflow-hidden hidden md:block">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Nome</th>
                        <th className="px-2 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Matrícula</th>
                        <th className="px-2 py-3 text-center text-xs font-medium text-slate-600 uppercase tracking-wider">Idade</th>
                        <th className="px-2 py-3 text-center text-xs font-medium text-slate-600 uppercase tracking-wider">Dt. Nasc.</th>
                        <th className="px-2 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Bairro</th>
                        <th className="px-2 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Responsável</th>
                        <th className="px-2 py-3 text-center text-xs font-medium text-slate-600 uppercase tracking-wider">Inscrito em</th>
                        <th className="px-2 py-3 text-center text-xs font-medium text-slate-600 uppercase tracking-wider">Turno</th>
                        <th className="px-2 py-3 text-center text-xs font-medium text-slate-600 uppercase tracking-wider">Origem</th>
                        <th className="px-2 py-3 text-center text-xs font-medium text-slate-600 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {activeChildrenReport.children.map((child, index) => (
                        <tr key={child.id} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                          <td className="px-3 py-2.5 text-sm font-medium text-slate-800">{child.name}</td>
                          <td className="px-2 py-2.5 text-xs text-slate-600">{child.enrollment_number || '-'}</td>
                          <td className="px-2 py-2.5 text-xs text-slate-600 text-center">{child.age || '-'}</td>
                          <td className="px-2 py-2.5 text-xs text-slate-600 text-center">{child.birth_date ? formatDate(child.birth_date) : '-'}</td>
                          <td className="px-2 py-2.5 text-xs text-slate-600">{child.neighborhood || '-'}</td>
                          <td className="px-2 py-2.5 text-xs text-slate-600">{child.guardian_name || '-'}</td>
                          <td className="px-2 py-2.5 text-xs text-slate-600 text-center">{child.created_at ? formatDate(child.created_at.split('T')[0]) : '-'}</td>
                          <td className="px-2 py-2.5 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              child.shift === 'Matutino' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {child.shift === 'Matutino' ? 'M' : 'T'}
                            </span>
                          </td>
                          <td className="px-2 py-2.5 text-center">
                            <span className="px-2 py-0.5 bg-slate-100 rounded text-xs">
                              {getOriginLabel(child.enrollment_origin)}
                            </span>
                          </td>
                          <td className="px-2 py-2.5 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              child.is_currently_active 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {child.is_currently_active ? 'Ativo' : 'Inativo'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {activeChildrenReport.children.length === 0 && (
                  <div className="p-8 text-center text-slate-500">
                    <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>Nenhuma criança ativa encontrada no período selecionado</p>
                  </div>
                )}
              </div>

              {/* Lista de Crianças - Mobile Cards */}
              <div className="md:hidden space-y-3">
                {activeChildrenReport.children.length === 0 ? (
                  <div className="bg-white rounded-xl shadow-md p-8 text-center text-slate-500 border border-slate-100">
                    <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>Nenhuma criança ativa encontrada no período selecionado</p>
                  </div>
                ) : (
                  activeChildrenReport.children.map((child) => (
                    <div key={child.id} className="bg-white rounded-xl shadow-sm border border-slate-100 p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-slate-800 text-sm">{child.name}</h4>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          child.is_currently_active 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {child.is_currently_active ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs mb-2">
                        {child.enrollment_number && (
                          <span className="text-slate-600">Mat: {child.enrollment_number}</span>
                        )}
                        {child.age && (
                          <span className="text-slate-600">{child.age} anos</span>
                        )}
                        {child.birth_date && (
                          <span className="text-slate-500">Nasc: {formatDate(child.birth_date)}</span>
                        )}
                        <span className={`px-2 py-0.5 rounded ${
                          child.shift === 'Matutino' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {child.shift === 'Matutino' ? 'Manhã' : 'Tarde'}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs mb-1">
                        {child.neighborhood && (
                          <span className="text-slate-600">Bairro: {child.neighborhood}</span>
                        )}
                        {child.guardian_name && (
                          <span className="text-slate-600">Resp: {child.guardian_name}</span>
                        )}
                        {child.created_at && (
                          <span className="text-slate-500">Inscrito: {formatDate(child.created_at.split('T')[0])}</span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs">
                        <span className="px-2 py-0.5 bg-slate-100 rounded text-slate-700">
                          {getOriginLabel(child.enrollment_origin)}
                        </span>
                        {child.activity_reason && (
                          <span className="text-slate-500">{child.activity_reason}</span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Birthday Reports */}
        <TabsContent value="birthdays" className="space-y-4">
          <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 border border-slate-100">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Relatório de Aniversariantes</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              {/* Período */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Data Inicial</label>
                <Input
                  type="date"
                  value={birthdayStartDate}
                  onChange={(e) => setBirthdayStartDate(e.target.value)}
                  data-testid="birthday-start-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Data Final</label>
                <Input
                  type="date"
                  value={birthdayEndDate}
                  onChange={(e) => setBirthdayEndDate(e.target.value)}
                  data-testid="birthday-end-date"
                />
              </div>
              
              {/* Turno */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Turno</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (birthdayShifts.includes('Matutino')) {
                        setBirthdayShifts(birthdayShifts.filter(s => s !== 'Matutino'));
                      } else {
                        setBirthdayShifts([...birthdayShifts, 'Matutino']);
                      }
                    }}
                    className={`px-3 py-2 rounded-lg text-sm ${
                      birthdayShifts.includes('Matutino') 
                        ? 'bg-amber-500 text-white' 
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    Manhã
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (birthdayShifts.includes('Vespertino')) {
                        setBirthdayShifts(birthdayShifts.filter(s => s !== 'Vespertino'));
                      } else {
                        setBirthdayShifts([...birthdayShifts, 'Vespertino']);
                      }
                    }}
                    className={`px-3 py-2 rounded-lg text-sm ${
                      birthdayShifts.includes('Vespertino') 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    Tarde
                  </button>
                </div>
              </div>
            </div>

            {/* Origem */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">Origem</label>
              <div className="flex flex-wrap gap-2">
                {origins.map(origin => (
                  <button
                    key={origin.value}
                    type="button"
                    onClick={() => {
                      if (birthdayOrigins.includes(origin.value)) {
                        setBirthdayOrigins(birthdayOrigins.filter(o => o !== origin.value));
                      } else {
                        setBirthdayOrigins([...birthdayOrigins, origin.value]);
                      }
                    }}
                    className={`px-3 py-2 rounded-lg text-sm ${
                      birthdayOrigins.includes(origin.value) 
                        ? 'bg-[#D87A4E] text-white' 
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {origin.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Dias que Frequentam */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">Dias que Frequentam</label>
              <div className="flex flex-wrap gap-2">
                {dayPatterns.map(pattern => (
                  <button
                    key={pattern.value}
                    type="button"
                    onClick={() => {
                      if (birthdayDayPatterns.includes(pattern.value)) {
                        setBirthdayDayPatterns(birthdayDayPatterns.filter(p => p !== pattern.value));
                      } else {
                        setBirthdayDayPatterns([...birthdayDayPatterns, pattern.value]);
                      }
                    }}
                    className={`px-3 py-2 rounded-lg text-sm ${
                      birthdayDayPatterns.includes(pattern.value) 
                        ? 'bg-green-500 text-white' 
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {pattern.label}
                  </button>
                ))}
              </div>
            </div>

            <Button 
              onClick={generateBirthdayReport}
              disabled={birthdayLoading}
              className="w-full sm:w-auto"
              data-testid="generate-birthday-report"
            >
              {birthdayLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <FileText className="h-4 w-4 mr-2" />
              )}
              Gerar Relatório
            </Button>
          </div>

          {/* Resultado do Relatório */}
          {birthdayReport && (
            <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 border border-slate-100">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">
                    Aniversariantes ({birthdayReport.children.length})
                  </h3>
                  <p className="text-sm text-slate-500">
                    Período: {new Date(birthdayReport.filters.startDate + 'T12:00:00').toLocaleDateString('pt-BR')} a {new Date(birthdayReport.filters.endDate + 'T12:00:00').toLocaleDateString('pt-BR')}
                  </p>
                </div>
                <Button onClick={exportBirthdayReportPDF} variant="outline" data-testid="export-birthday-pdf">
                  <Download className="h-4 w-4 mr-2" />
                  Exportar PDF
                </Button>
              </div>

              {/* Filtros aplicados */}
              <div className="flex flex-wrap gap-2 mb-4 text-xs">
                <span className="px-2 py-1 bg-slate-100 rounded text-slate-600">
                  Origem: {birthdayReport.filters.origins.map(o => {
                    const found = origins.find(x => x.value === o);
                    return found ? found.label : o;
                  }).join(', ') || 'Todas'}
                </span>
                <span className="px-2 py-1 bg-slate-100 rounded text-slate-600">
                  Turno: {birthdayReport.filters.shifts.map(s => s === 'Matutino' ? 'Manhã' : 'Tarde').join(', ') || 'Todos'}
                </span>
                <span className="px-2 py-1 bg-slate-100 rounded text-slate-600">
                  Dias: {birthdayReport.filters.dayPatterns.map(p => {
                    const found = dayPatterns.find(x => x.value === p);
                    return found ? found.label : p;
                  }).join(', ') || 'Todos'}
                </span>
              </div>

              {/* Lista de aniversariantes */}
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {birthdayReport.children.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    Nenhum aniversariante encontrado com os filtros selecionados
                  </div>
                ) : (
                  birthdayReport.children.map((child, index) => (
                    <div 
                      key={child.id}
                      className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl border border-slate-200"
                    >
                      {/* Avatar/Foto */}
                      <div className="flex-shrink-0">
                        <ChildAvatar 
                          photo={child.photo_url} 
                          name={child.name} 
                          size="lg"
                          zoomable={true}
                          className="border-2 border-amber-300"
                        />
                      </div>
                      
                      {/* Informações */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 truncate">{child.name}</p>
                        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            🎂 {formatBirthdayDate(child.birth_date)}
                          </span>
                          <span>•</span>
                          <span>{child.age} anos</span>
                        </div>
                      </div>
                      
                      {/* Badges */}
                      <div className="flex flex-col gap-1 items-end text-xs">
                        <span className={`px-2 py-0.5 rounded ${
                          child.shift === 'Matutino' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {child.shift === 'Matutino' ? 'Manhã' : 'Tarde'}
                        </span>
                        <span className="px-2 py-0.5 bg-slate-100 rounded text-slate-600">
                          {getOriginLabel(child.enrollment_origin)}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Lista de Crianças Customizada */}
        <TabsContent value="child-list" className="space-y-4" data-testid="child-list-tab">
          <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 border border-slate-200">
            <h3 className="font-semibold text-lg text-slate-800 mb-4">Lista de Crianças Personalizada</h3>
            
            {/* Seleção de Colunas */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">Colunas a exibir</label>
              <div className="flex flex-wrap gap-2">
                {childListColumnOptions.map(col => (
                  <button
                    key={col.value}
                    type="button"
                    onClick={() => {
                      const newCols = childListColumns.includes(col.value)
                        ? childListColumns.filter(c => c !== col.value)
                        : [...childListColumns, col.value];
                      setChildListColumns(newCols);
                    }}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      childListColumns.includes(col.value)
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    {col.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Filtros */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Status (Ativo/Inativo) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Status</label>
                <div className="flex flex-wrap gap-1">
                  <button
                    type="button"
                    onClick={() => setChildListStatus('active')}
                    className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                      childListStatus === 'active'
                        ? 'bg-green-500 text-white border-green-500'
                        : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    Ativos
                  </button>
                  <button
                    type="button"
                    onClick={() => setChildListStatus('inactive')}
                    className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                      childListStatus === 'inactive'
                        ? 'bg-red-500 text-white border-red-500'
                        : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    Inativos
                  </button>
                  <button
                    type="button"
                    onClick={() => setChildListStatus('both')}
                    className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                      childListStatus === 'both'
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    Todos
                  </button>
                </div>
              </div>

              {/* Origem */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Origem</label>
                <div className="flex flex-wrap gap-1">
                  {origins.map(origin => (
                    <button
                      key={origin.value}
                      type="button"
                      onClick={() => {
                        const newOrigins = childListOrigins.includes(origin.value)
                          ? childListOrigins.filter(o => o !== origin.value)
                          : [...childListOrigins, origin.value];
                        setChildListOrigins(newOrigins);
                      }}
                      className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                        childListOrigins.includes(origin.value)
                          ? 'bg-green-500 text-white border-green-500'
                          : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {origin.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Turno */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Turno</label>
                <div className="flex flex-wrap gap-1">
                  {shifts.map(shift => (
                    <button
                      key={shift.value}
                      type="button"
                      onClick={() => {
                        const newShifts = childListShifts.includes(shift.value)
                          ? childListShifts.filter(s => s !== shift.value)
                          : [...childListShifts, shift.value];
                        setChildListShifts(newShifts);
                      }}
                      className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                        childListShifts.includes(shift.value)
                          ? 'bg-amber-500 text-white border-amber-500'
                          : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {shift.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Dias */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Dias</label>
                <div className="flex flex-wrap gap-1">
                  {dayPatterns.map(pattern => (
                    <button
                      key={pattern.value}
                      type="button"
                      onClick={() => {
                        const newDays = childListDays.includes(pattern.value)
                          ? childListDays.filter(d => d !== pattern.value)
                          : [...childListDays, pattern.value];
                        setChildListDays(newDays);
                      }}
                      className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                        childListDays.includes(pattern.value)
                          ? 'bg-purple-500 text-white border-purple-500'
                          : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {pattern.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Gênero */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Gênero</label>
                <div className="flex flex-wrap gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      const newGenders = childListGenders.includes('MASCULINO')
                        ? childListGenders.filter(g => g !== 'MASCULINO')
                        : [...childListGenders, 'MASCULINO'];
                      setChildListGenders(newGenders);
                    }}
                    className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                      childListGenders.includes('MASCULINO')
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    Masculino
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const newGenders = childListGenders.includes('FEMININO')
                        ? childListGenders.filter(g => g !== 'FEMININO')
                        : [...childListGenders, 'FEMININO'];
                      setChildListGenders(newGenders);
                    }}
                    className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                      childListGenders.includes('FEMININO')
                        ? 'bg-pink-500 text-white border-pink-500'
                        : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    Feminino
                  </button>
                </div>
              </div>

              {/* Idade */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">Idade</label>
                <div className="flex flex-wrap gap-1">
                  {[6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(age => (
                    <button
                      key={age}
                      type="button"
                      onClick={() => {
                        const newAges = childListAges.includes(age)
                          ? childListAges.filter(a => a !== age)
                          : [...childListAges, age];
                        setChildListAges(newAges);
                      }}
                      className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                        childListAges.includes(age)
                          ? 'bg-teal-500 text-white border-teal-500'
                          : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {age}
                    </button>
                  ))}
                  <span className="text-xs text-slate-500 ml-2 self-center">
                    {childListAges.length === 0 ? '(Todas)' : `(${childListAges.length} selecionadas)`}
                  </span>
                </div>
              </div>
            </div>

            {/* Ordenação */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Ordenar por</label>
                <select
                  value={childListSortBy}
                  onChange={(e) => setChildListSortBy(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {childListSortOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Ordem</label>
                <select
                  value={childListSortOrder}
                  onChange={(e) => setChildListSortOrder(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="asc">Crescente</option>
                  <option value="desc">Decrescente</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={generateChildListReport}
                  disabled={childListLoading || childListColumns.length === 0}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {childListLoading ? 'Gerando...' : 'Gerar Lista'}
                </button>
              </div>
            </div>
          </div>

          {/* Resultado do Relatório */}
          {childListReport && (
            <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 border border-slate-200">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-semibold text-slate-800">
                  {childListReport.children.length} crianças encontradas
                </h4>
                <button
                  onClick={exportChildListPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                >
                  <FileText className="h-4 w-4" />
                  Exportar PDF
                </button>
              </div>

              {/* Tabela de resultados */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-100">
                      {childListColumns.map(col => {
                        const option = childListColumnOptions.find(o => o.value === col);
                        return (
                          <th key={col} className="px-3 py-2 text-left font-medium text-slate-700 whitespace-nowrap">
                            {option ? option.label : col}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {childListReport.children.map((child, idx) => (
                      <tr key={child.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        {childListColumns.map(col => (
                          <td key={col} className="px-3 py-2 text-slate-700 whitespace-nowrap">
                            {formatChildListValue(child, col)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Relatos das Crianças */}
        <TabsContent value="relatos" className="space-y-4">
          <RelatosReport />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Componente de Relatos para a página de Relatórios
const RelatosReport = () => {
  const [reports, setReports] = useState([]);
  const [children, setChildren] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { user, isAdmin } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reportsRes, childrenRes] = await Promise.all([
        axios.get(`${API}/children/reports/all`),
        axios.get(`${API}/children`)
      ]);
      setReports(reportsRes.data || []);
      const childMap = {};
      (childrenRes.data || []).forEach(c => { childMap[c.id] = c; });
      setChildren(childMap);
    } catch (error) {
      console.log('Erro ao carregar relatos');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (reportId) => {
    try {
      await axios.post(`${API}/children/reports/${reportId}/mark-read`);
      setReports(prev => prev.map(r =>
        r.id === reportId
          ? { ...r, read_by: [...(r.read_by || []), user.username] }
          : r
      ));
    } catch (error) {
      console.log('Erro ao marcar como lido');
    }
  };

  const deleteReport = async (reportId) => {
    if (!confirm('Tem certeza que deseja excluir este relato?')) return;
    try {
      await axios.delete(`${API}/children/reports/${reportId}`);
      setReports(prev => prev.filter(r => r.id !== reportId));
      toast.success('Relato excluído');
    } catch (error) {
      toast.error('Erro ao excluir relato');
    }
  };

  const exportChildPdf = (childId) => {
    const child = children[childId];
    const childReports = grouped[childId];
    if (!childReports || childReports.length === 0) return;

    const childName = child?.name || childReports[0]?.child_name || 'Criança';
    const doc = new jsPDF();

    // Logo
    let yPos = addPdfHeader(doc);

    // Cabeçalho
    doc.setFontSize(16);
    doc.setTextColor(88, 28, 135); // purple-900
    doc.text('Relatos - ' + childName, 14, yPos);

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    let infoLine = '';
    if (child?.age) infoLine += `Idade: ${child.age} anos`;
    if (child?.shift) infoLine += `  |  Turno: ${child.shift === 'Matutino' ? 'Manhã' : 'Tarde'}`;
    if (child?.enrollment_number) infoLine += `  |  Matrícula: ${child.enrollment_number}`;
    if (child?.guardian_name) infoLine += `  |  Responsável: ${child.guardian_name}`;
    if (infoLine) doc.text(infoLine, 14, yPos + 8);

    doc.setDrawColor(168, 85, 247); // purple-500
    doc.line(14, yPos + 11, 196, yPos + 11);

    // Tabela de relatos
    const tableData = childReports.map(r => [
      new Date(r.created_at).toLocaleDateString('pt-BR') + '\n' +
        new Date(r.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
      r.content,
      r.created_by
    ]);

    autoTable(doc, {
      startY: yPos + 15,
      head: [['Data', 'Relato', 'Autor']],
      body: tableData,
      headStyles: { fillColor: [147, 51, 234], textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 8, cellPadding: 4 },
      columnStyles: {
        0: { cellWidth: 28 },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 30 }
      },
      styles: { overflow: 'linebreak' },
      margin: { left: 14, right: 14, top: getPdfHeaderHeight(doc) },
      didDrawPage: (data) => { if (data.pageNumber > 1) addPdfHeader(doc); }
    });

    // Rodapé
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`Gerado em ${new Date().toLocaleDateString('pt-BR')} - Página ${i}/${pageCount}`, 14, doc.internal.pageSize.height - 10);
    }

    doc.save(`relatos_${childName.replace(/\s+/g, '_').toLowerCase()}.pdf`);
    toast.success(`PDF de ${childName} exportado!`);
  };

  const exportAllPdf = () => {
    if (filteredChildIds.length === 0) return;

    const doc = new jsPDF();
    let isFirst = true;

    filteredChildIds.forEach(childId => {
      const child = children[childId];
      const childReports = grouped[childId];
      if (!childReports || childReports.length === 0) return;

      if (!isFirst) doc.addPage();
      isFirst = false;

      const childName = child?.name || childReports[0]?.child_name || 'Criança';
      
      // Logo
      let yPos = addPdfHeader(doc);

      // Cabeçalho da criança
      doc.setFontSize(14);
      doc.setTextColor(88, 28, 135);
      doc.text(childName, 14, yPos);

      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      let infoLine = '';
      if (child?.age) infoLine += `Idade: ${child.age} anos`;
      if (child?.shift) infoLine += `  |  Turno: ${child.shift === 'Matutino' ? 'Manhã' : 'Tarde'}`;
      if (child?.enrollment_number) infoLine += `  |  Mat.: ${child.enrollment_number}`;
      if (child?.guardian_name) infoLine += `  |  Resp.: ${child.guardian_name}`;
      if (infoLine) doc.text(infoLine, 14, yPos + 6);

      doc.setDrawColor(168, 85, 247);
      doc.line(14, yPos + 9, 196, yPos + 9);

      const tableData = childReports.map(r => [
        new Date(r.created_at).toLocaleDateString('pt-BR') + '\n' +
          new Date(r.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        r.content,
        r.created_by
      ]);

      autoTable(doc, {
        startY: yPos + 12,
        head: [['Data', 'Relato', 'Autor']],
        body: tableData,
        headStyles: { fillColor: [147, 51, 234], textColor: 255, fontSize: 9 },
        bodyStyles: { fontSize: 8, cellPadding: 4 },
        columnStyles: {
          0: { cellWidth: 28 },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 30 }
        },
        styles: { overflow: 'linebreak' },
        margin: { left: 14, right: 14, top: getPdfHeaderHeight(doc) },
        didDrawPage: (data) => { if (data.pageNumber > 1) addPdfHeader(doc); }
      });
    });

    // Header e rodapé em todas as páginas
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`Gerado em ${new Date().toLocaleDateString('pt-BR')} - Página ${i}/${pageCount}`, 14, doc.internal.pageSize.height - 10);
    }

    doc.save('relatos_todas_criancas.pdf');
    toast.success('PDF exportado com todos os relatos!');
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" /></div>;
  }

  // Agrupar por criança
  const grouped = {};
  reports.forEach(r => {
    if (!grouped[r.child_id]) grouped[r.child_id] = [];
    grouped[r.child_id].push(r);
  });

  // Filtrar por nome
  const filteredChildIds = Object.keys(grouped).filter(cid => {
    const child = children[cid];
    const name = child?.name || grouped[cid][0]?.child_name || '';
    return matchesSearch(name, searchTerm);
  });

  // Ordenar por nome
  filteredChildIds.sort((a, b) => {
    const nameA = children[a]?.name || grouped[a][0]?.child_name || '';
    const nameB = children[b]?.name || grouped[b][0]?.child_name || '';
    return nameA.localeCompare(nameB);
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-600">
          {reports.length} relato{reports.length !== 1 ? 's' : ''} de {Object.keys(grouped).length} criança{Object.keys(grouped).length !== 1 ? 's' : ''}
        </p>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Buscar por nome..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-xs"
            data-testid="relatos-search"
          />
          {filteredChildIds.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={exportAllPdf}
              className="text-purple-600 border-purple-300 hover:bg-purple-50 whitespace-nowrap"
              data-testid="export-all-relatos-pdf"
            >
              <FileDown className="h-4 w-4 mr-1" />
              Exportar Todos (PDF)
            </Button>
          )}
        </div>
      </div>

      {filteredChildIds.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <MessageSquare className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p>Nenhum relato encontrado</p>
        </div>
      ) : (
        filteredChildIds.map(childId => {
          const child = children[childId];
          const childReports = grouped[childId];
          return (
            <div key={childId} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`relato-group-${childId}`}>
              {/* Cabeçalho da criança */}
              <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-purple-100">
                <ChildAvatar
                  photo={child?.photo_url}
                  name={child?.name || childReports[0]?.child_name}
                  size="md"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-800">{child?.name || childReports[0]?.child_name}</h3>
                  <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                    {child?.age && <span>{child.age} anos</span>}
                    {child?.shift && <span>{child.shift === 'Matutino' ? 'Manhã' : 'Tarde'}</span>}
                    {child?.enrollment_number && <span>Mat. {child.enrollment_number}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => exportChildPdf(childId)}
                    className="text-xs text-purple-600 hover:bg-purple-100 px-2 py-1 rounded flex items-center gap-1"
                    title="Exportar relatos desta criança em PDF"
                    data-testid={`export-relato-pdf-${childId}`}
                  >
                    <FileDown className="h-3.5 w-3.5" />
                    PDF
                  </button>
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-medium">
                    {childReports.length} relato{childReports.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              {/* Lista de relatos */}
              <div className="divide-y divide-slate-100">
                {childReports.map(report => {
                  const isUnread = !(report.read_by || []).includes(user.username);
                  const isAuthor = report.created_by_id === user.id;
                  return (
                    <div key={report.id} className={`p-4 ${isUnread ? 'bg-purple-50/50' : ''}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">{report.content}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                            <span>Por: {report.created_by}</span>
                            <span>{new Date(report.created_at).toLocaleDateString('pt-BR')} às {new Date(report.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                            {isUnread && <span className="text-purple-600 font-medium">Novo</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {isUnread && (
                            <button onClick={() => markAsRead(report.id)} className="p-1.5 text-purple-600 hover:bg-purple-100 rounded" title="Marcar como lido">
                              <CheckCircle className="h-4 w-4" />
                            </button>
                          )}
                          {(isAuthor || isAdmin()) && (
                            <button onClick={() => deleteReport(report.id)} className="p-1.5 text-red-500 hover:bg-red-100 rounded" title="Excluir">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default Reports;